import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageHeader, EmptyState } from '@/components/primitives';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { GraduationCap, PlusCircle, Sparkles, Save, Send, Loader2, Trash2, UserPlus, X } from 'lucide-react';
import { fmtDate } from '@/lib/format';

function OnboardedPicker({ companyId, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open) return;
    api.get(`/onboardings/recent-recipients?company_id=${companyId}`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, [open, companyId]);

  const filtered = useMemo(() => {
    if (!q) return items;
    const ql = q.toLowerCase();
    return items.filter((i) => (i.name || '').toLowerCase().includes(ql) || (i.email || '').toLowerCase().includes(ql) || (i.role || '').toLowerCase().includes(ql));
  }, [items, q]);

  const toggle = (email) => {
    if (selected.includes(email)) onChange(selected.filter((x) => x !== email));
    else onChange([...selected, email]);
  };
  const selectAll = () => onChange(Array.from(new Set([...selected, ...filtered.map((i) => i.email)])));
  const clearAll = () => onChange(selected.filter((e) => !filtered.some((i) => i.email === e)));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="training-pick-onboarded"><UserPlus className="h-3.5 w-3.5 mr-1" />Pick from recent onboardings ({selected.length})</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Recently onboarded (last 30 days)</DialogTitle>
          <DialogDescription>Pick which newly joined employees should receive this training email.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or role" data-testid="onboarded-search" />
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{filtered.length} recent joiners</span>
            <div className="flex gap-2">
              <button className="text-primary hover:underline" onClick={selectAll} data-testid="onboarded-select-all">Select all</button>
              <button className="text-muted-foreground hover:underline" onClick={clearAll}>Clear</button>
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto rounded-lg border">
            {filtered.length === 0 && <p className="text-xs text-muted-foreground p-4 text-center">No recent onboardings match your search.</p>}
            {filtered.map((i) => (
              <label key={i.id} className="flex items-start gap-3 p-3 border-b last:border-b-0 hover:bg-accent cursor-pointer">
                <Checkbox checked={selected.includes(i.email)} onCheckedChange={() => toggle(i.email)} data-testid={`onboarded-pick-${i.id}`} className="mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{i.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{i.email} · {i.role || '—'}</p>
                </div>
                <span className="text-[11px] text-muted-foreground shrink-0">{fmtDate(i.onboarded_at)}</span>
              </label>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={() => setOpen(false)} data-testid="onboarded-picker-done">Done ({selected.length})</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TrainingPage() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);

  // Send options
  const [allStaff, setAllStaff] = useState(false);
  const [onboardedRecipients, setOnboardedRecipients] = useState([]); // manually picked emails from recent onboardings
  const [extraRecipients, setExtraRecipients] = useState('');
  const [extraCc, setExtraCc] = useState('');
  const [subject, setSubject] = useState('');

  const load = async () => {
    const r = await api.get(`/trainings?company_id=${companyId}`);
    setItems(r.data);
    if (r.data.length && !selected) setSelected(r.data[0]);
  };
  useEffect(() => { api.get(`/companies/${companyId}`).then((r) => setCompany(r.data)); load(); }, [companyId]);
  useEffect(() => {
    setSubject(selected ? `Training: ${selected.title}` : '');
    setOnboardedRecipients([]);
  }, [selected?.id]);

  const create = async () => {
    const title = prompt('Training title');
    if (!title) return;
    try {
      const r = await api.post('/trainings', { company_id: companyId, title, description: '', content: '' });
      await load();
      setSelected(r.data);
      toast.success('Created');
    } catch (e) { toast.error('Failed'); }
  };

  const draft = async () => {
    if (!selected) return;
    setDrafting(true);
    try {
      const r = await api.post('/ai/draft-training', { company_id: companyId, topic: selected.title, audience: selected.audience || 'new hires', duration_minutes: selected.duration_minutes || 120 });
      setSelected({ ...selected, content: r.data.content });
      toast.success('AI draft ready');
    } catch (e) { toast.error('AI failed'); }
    finally { setDrafting(false); }
  };

  const save = async () => {
    if (!selected) return;
    try {
      await api.patch(`/trainings/${selected.id}`, { title: selected.title, description: selected.description, content: selected.content, audience: selected.audience, duration_minutes: selected.duration_minutes });
      toast.success('Saved');
      load();
    } catch (e) { toast.error('Save failed'); }
  };

  const del = async () => {
    if (!selected || !window.confirm('Delete training?')) return;
    await api.delete(`/trainings/${selected.id}`);
    setSelected(null);
    load();
  };

  const send = async () => {
    if (!selected) return;
    const manual = extraRecipients.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    const finalRecipients = Array.from(new Set([...(onboardedRecipients || []), ...manual]));
    if (finalRecipients.length === 0 && !allStaff) {
      return toast.error('Add at least one recipient or check "All staff"');
    }
    setSending(true);
    try {
      const r = await api.post(`/trainings/${selected.id}/send`, {
        recipients: finalRecipients,
        cc: extraCc.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
        include_all_staff: allStaff,
        include_new_hires: false, // deprecated in favor of manual picker
        subject,
      });
      const status = r.data.result?.status;
      if (status === 'sent') toast.success(`Sent to ${r.data.recipients_count} recipients`);
      else if (status === 'queued') toast.message(`Queued - SMTP not configured. Recipients: ${r.data.recipients_count}`);
      else toast.error('Send failed: ' + (r.data.result?.reason || 'unknown'));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSending(false); }
  };

  return (
    <>
      <PageHeader
        title={`${company?.name || '…'} · Staff Training`}
        description="AI-drafted training modules you can email to newly onboarded joiners or all staff."
        actions={<Button onClick={create} data-testid="add-training-button"><PlusCircle className="h-4 w-4 mr-2" />New program</Button>}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-6 grid gap-4 lg:grid-cols-[280px_1fr]">
        <div className="space-y-2">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Create a training program to get started.</p>
          ) : items.map((t) => (
            <button
              key={t.id}
              onClick={() => setSelected(t)}
              className={`w-full text-left rounded-lg border p-3 hover:bg-accent transition-colors ${selected?.id === t.id ? 'bg-accent border-primary/40' : 'bg-card'}`}
              data-testid={`training-item-${t.id}`}
            >
              <p className="text-sm font-medium truncate">{t.title}</p>
              <p className="text-xs text-muted-foreground">{(t.send_history || []).length} sends</p>
            </button>
          ))}
        </div>
        <div>
          {!selected ? (
            <EmptyState icon={GraduationCap} title="Select a training program" description="Or create one from the top-right button." />
          ) : (
            <Card className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Title</Label><Input value={selected.title || ''} onChange={(e) => setSelected({ ...selected, title: e.target.value })} data-testid="training-title-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Audience</Label><Input value={selected.audience || 'new hires'} onChange={(e) => setSelected({ ...selected, audience: e.target.value })} /></div>
                <div className="space-y-1 md:col-span-2"><Label className="text-xs">Description</Label><Input value={selected.description || ''} onChange={(e) => setSelected({ ...selected, description: e.target.value })} /></div>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Content (Markdown)</Label>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={draft} disabled={drafting} data-testid="training-ai-draft">{drafting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />} AI draft</Button>
                    <Button size="sm" onClick={save} data-testid="training-save-button"><Save className="h-3.5 w-3.5 mr-1" />Save</Button>
                    <Button size="sm" variant="ghost" onClick={del} data-testid="training-delete-button"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                <Textarea rows={16} value={selected.content || ''} onChange={(e) => setSelected({ ...selected, content: e.target.value })} className="font-mono text-sm" data-testid="training-content-textarea" />
              </div>
              <div className="rounded-lg border bg-muted/40 p-3 space-y-2">
                <p className="text-xs font-semibold">Send training</p>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={allStaff} onCheckedChange={(v) => setAllStaff(!!v)} data-testid="training-all-staff" /> All active staff
                  </label>
                  <span className="text-xs text-muted-foreground">·</span>
                  <OnboardedPicker companyId={companyId} selected={onboardedRecipients} onChange={setOnboardedRecipients} />
                </div>
                {onboardedRecipients.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {onboardedRecipients.slice(0, 8).map((email) => (
                      <Badge key={email} variant="secondary" className="text-[11px] gap-1">
                        {email}
                        <button
                          onClick={() => setOnboardedRecipients(onboardedRecipients.filter((x) => x !== email))}
                          className="hover:text-destructive"
                          data-testid={`remove-recipient-${email}`}
                          aria-label={`Remove ${email}`}
                        ><X className="h-3 w-3" /></button>
                      </Badge>
                    ))}
                    {onboardedRecipients.length > 8 && (
                      <Badge variant="secondary" className="text-[11px]">+{onboardedRecipients.length - 8} more</Badge>
                    )}
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-2">
                  <div className="space-y-1"><Label className="text-xs">Additional recipients</Label><Input value={extraRecipients} onChange={(e) => setExtraRecipients(e.target.value)} placeholder="comma or newline separated" data-testid="training-recipients-extra" /></div>
                  <div className="space-y-1"><Label className="text-xs">Additional CC</Label><Input value={extraCc} onChange={(e) => setExtraCc(e.target.value)} placeholder="director@company.com" /></div>
                </div>
                <div className="space-y-1"><Label className="text-xs">Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="training-subject" /></div>
                <div className="flex justify-end">
                  <Button onClick={send} disabled={sending} data-testid="training-send-button">
                    {sending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}Send emails
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">Company default CC recipients are always added automatically.</p>
              </div>
              {selected.send_history && selected.send_history.length > 0 && (
                <div className="text-xs">
                  <p className="font-semibold mb-1">Send history</p>
                  <ul className="space-y-1 text-muted-foreground">
                    {selected.send_history.slice(-5).reverse().map((h) => (
                      <li key={h.id}>{new Date(h.timestamp).toLocaleString()} · to {h.to.length} · {h.status}</li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
